<script setup lang="ts">
import { computed, type CSSProperties } from 'vue';

export type ButtonVariant = 'filled' | 'outlined' | 'text';

export interface ButtonStyles {
  actionColor?: string;
  textColor?: string;
}

export interface PlsButtonProps {
  disabled?: boolean;
  size?: 'default' | 'small';
  variant?: ButtonVariant;
  styles?: ButtonStyles;
  disabledStyles?: ButtonStyles;
}

const props = withDefaults(defineProps<PlsButtonProps>(), {
  disabled: false,
  size: 'default',
  variant: 'filled',
});

const emit = defineEmits<{
  (event: 'click'): void;
}>();

const getStylesForVariant = (styles: ButtonStyles | undefined, variant: ButtonVariant): CSSProperties => {
  if (!styles) return {};

  const { actionColor, textColor } = styles;

  switch (variant) {
    case 'filled':
      return {
        backgroundColor: actionColor,
        borderColor: actionColor,
        color: textColor,
      };
    case 'outlined':
      return {
        backgroundColor: 'transparent',
        borderColor: actionColor,
        color: textColor,
      };
    case 'text':
      return {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: textColor,
      };
    default:
      return {};
  }
};

const computedStyles = computed<CSSProperties>(() => {
  if (props.disabled && props.disabledStyles) {
    return getStylesForVariant(props.disabledStyles, props.variant);
  }

  return getStylesForVariant(props.styles, props.variant);
});

const sizeClass = computed(() => {
  return props.size === 'small' ? 'pls-button--small' : 'pls-button--default';
});

const variantClass = computed(() => {
  return `pls-button--${props.variant}`;
});
</script>

<template>
  <button
    :class="['pls-button', 'theme-font-main', 'btn-radius', sizeClass, variantClass]"
    :style="computedStyles"
    :disabled="disabled"
    v-on="$listeners"
  >
    <slot />
  </button>
</template>

<style scoped lang="scss">
.pls-button {
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  width: 100%;
  border-style: solid;
  border-width: 1px;

  &:disabled {
    cursor: not-allowed;
  }
}

.pls-button--default {
  padding: 12px;
}

.pls-button--small {
  padding: 8px;
}

.pls-button--filled {
  border-color: transparent;
}

.pls-button--outlined {
  background-color: transparent;
}

.pls-button--text {
  background-color: transparent;
  border-color: transparent;
}
</style>
